import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { OperacionAFOCLTablaValorVista, OperacionAFOCLTablaValorVistaInicial } from '../types/dto';
import { OperacionAFOCLTablaValorFields } from '../types/fields';
import { HttpAFOCL } from '../api/httoAFOCL';

export interface AFOCLRow {
  codInstrumento: number;
  descInstrumento: string;
  ordenInstrumento: number;
  celdas: Record<number, OperacionAFOCLTablaValorVistaInicial>;
}


export interface AFOCLHeader {
  codOperacionTipo: number;
  descOperacionTipo: string;
  ordenOperacionTipo: number;
}


export const pivotAFOCLData = (flatData: OperacionAFOCLTablaValorVistaInicial[]): { headers: AFOCLHeader[], rows: AFOCLRow[] } => {
  if (!flatData || !flatData.length) return { headers: [], rows: [] };

  const headersMap = new Map<number, AFOCLHeader>();
  const rowsMap = new Map<number, AFOCLRow>();

  flatData.forEach((item) => {
    const codTipo = item[OperacionAFOCLTablaValorFields.CodOperacionTipo];
    const codInst = item[OperacionAFOCLTablaValorFields.CodInstrumento];

    if (!headersMap.has(codTipo)) {
      headersMap.set(codTipo, {
        codOperacionTipo: codTipo,
        descOperacionTipo: item[OperacionAFOCLTablaValorFields.DescOperacionTipo],
        ordenOperacionTipo: item[OperacionAFOCLTablaValorFields.OrdenOperacionTipo] ?? 0
      });
    }

    if (!rowsMap.has(codInst)) {
      rowsMap.set(codInst, {
        codInstrumento: codInst,
        descInstrumento: item[OperacionAFOCLTablaValorFields.DescInstrumento],
        ordenInstrumento: item[OperacionAFOCLTablaValorFields.OrdenInstrumento] ?? 0,
        celdas: {}
      });
    }

    // Guardamos todo el item aquí
    rowsMap.get(codInst)!.celdas[codTipo] = item;
  });

  const headers = Array.from(headersMap.values()).sort((a, b) => a.ordenOperacionTipo - b.ordenOperacionTipo);
  const rows = Array.from(rowsMap.values()).sort((a, b) => a.ordenInstrumento - b.ordenInstrumento);

  return { headers, rows };
};

// pivot de los datos originales
export const useAFOCLTablaValorInicial = (codUnidadNegocio: number | undefined | null, codSectorComercial: number | undefined | null) => {
  const {
    data: tablaValorOriginal = [],
    isLoading,
    isError, 
    error
  } = useQuery<OperacionAFOCLTablaValorVistaInicial[], Error>({
    queryKey: ['AFOCL-tabla-valor-inicial', codUnidadNegocio, codSectorComercial],
    queryFn: () => HttpAFOCL.getTablaValoresInicial(codUnidadNegocio!, codSectorComercial!),
    enabled: !!(codUnidadNegocio && codSectorComercial) 
  });

  const pivotData = useMemo(() => pivotAFOCLData(tablaValorOriginal), [tablaValorOriginal]);

  return {
    headers: pivotData.headers,
    rows: pivotData.rows,
    aforos: tablaValorOriginal,
    isLoading,
    isError,
    error
  };
};


export const useAFOCLTablaValor = (idOperacion: number | undefined | null) => {
  const {
    data: tablaValorData = [],
    isLoading,
    isError,
    error,
    refetch 
  } = useQuery<OperacionAFOCLTablaValorVista[], Error>({
    queryKey: ['AFOCL-tabla-valor', idOperacion],
    queryFn: () => HttpAFOCL.getTablaValores(idOperacion!),
    enabled: !!idOperacion
  });

  const pivotData = useMemo(() => pivotAFOCLData(tablaValorData), [tablaValorData]);

  return {
    headers: pivotData.headers,
    rows: pivotData.rows,
    aforos: tablaValorData,
    isLoading,
    isError,
    error,
    refetch
  };
};
